/**
 * IncomingCallOverlay — the incoming-call screen.
 *
 * It answers three questions immediately and truthfully: WHO is calling, for
 * WHICH booking, and what this device is doing about it. When the browser has
 * refused to play the ring tone the banner says so and offers an explicit
 * gesture instead of pretending the phone is ringing.
 *
 * Deciding has TWO routes, and neither forces the other:
 *
 *   1. ONE tap on Decline ends the call attempt and takes no reason — exactly as
 *      certified. A patient in the middle of something must never be walked
 *      through a dialog before they can hang up.
 *   2. "Add a reason" opens the optional reasons. Picking one declines AND
 *      explains in a single tap. `Cancel` closes the reasons and puts the call
 *      BACK to ringing — opening the reasons is not a decision, so it must not
 *      cost the patient the call.
 *
 * The chosen reason travels as a stable CODE — see utils/callDecline — so the
 * caller is told "Call declined — they're in a meeting" instead of watching a
 * silent "Waiting for patient…" until the ring times out. Free text is offered
 * for "Other" only and is capped at DECLINE_NOTE_MAX_LENGTH.
 *
 * NOTE for the e2e contract: no control other than the primary Decline button
 * carries the word "Decline" in its accessible name, so
 * `getByRole('button', { name: 'Decline' })` still resolves to exactly one.
 *
 * Purely presentational: every decision (validation, idempotency, timeout,
 * routing, delivery) lives in `useIncomingCall` / `utils/incomingCall` /
 * `utils/callDecline`, so this can be reviewed as UI alone.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Phone, PhoneOff, Volume2, AlertTriangle, Stethoscope, User, ArrowLeft, X } from 'lucide-react';
import { useLanguage } from '../../i18n/LanguageContext';
import type { IncomingCall } from '../../utils/incomingCall';
import {
  DECLINE_NOTE_MAX_LENGTH,
  DECLINE_QUICK_REASON_KEYS,
  type CallDeclineReasonCode,
} from '../../utils/callDecline';

/** Roles we can name; anything else is shown without a label rather than lied about. */
const ROLE_LABEL_KEY: Record<string, string> = {
  patient: 'incomingCall.patient',
  doctor: 'incomingCall.doctor',
  nurse: 'incomingCall.nurse',
  admin: 'incomingCall.admin',
};

interface Props {
  call: IncomingCall;
  /** True when the browser refused to play the ring tone. */
  ringBlocked: boolean;
  onAccept: () => void;
  /** `reasonCode` defaults to NO_REASON; `note` is only meaningful for OTHER. */
  onDecline: (reasonCode?: CallDeclineReasonCode, note?: string | null) => void;
  onEnableSound: () => void;
  /** Stop the ring tone without answering or declining (writing a reason). */
  onSilence: () => void;
}

const IncomingCallOverlay: React.FC<Props> = ({
  call, ringBlocked, onAccept, onDecline, onEnableSound, onSilence,
}) => {
  const { t } = useLanguage();
  const acceptRef = useRef<HTMLButtonElement>(null);
  const noteRef = useRef<HTMLTextAreaElement>(null);
  const reasonsRef = useRef<HTMLDivElement>(null);
  /** The optional reasons are open. The call is undecided until a reason is sent. */
  const [reasonOpen, setReasonOpen] = useState(false);
  /** The free-text field for OTHER is open (inside the reasons panel). */
  const [otherOpen, setOtherOpen] = useState(false);
  const [note, setNote] = useState('');

  // Move focus to the primary action so the call can be answered from the
  // keyboard without hunting for it.
  useEffect(() => {
    acceptRef.current?.focus();
  }, []);

  // The note field is the only thing the user must type into, so it takes focus
  // when it opens; otherwise the first reason chip does.
  useEffect(() => {
    if (otherOpen) {
      noteRef.current?.focus();
      return;
    }
    if (reasonOpen) reasonsRef.current?.querySelector<HTMLButtonElement>('button')?.focus();
  }, [reasonOpen, otherOpen]);

  const roleLabelKey = ROLE_LABEL_KEY[call.fromRole];
  const caller = call.fromName || (roleLabelKey ? t(roleLabelKey) : '') || t('incomingCall.someone');

  /**
   * Opening the reasons stops the ring tone (the user has clearly stopped
   * engaging with the call), but decides NOTHING: the decline is only sent when
   * they choose a reason.
   */
  const openReasons = () => {
    onSilence();
    setReasonOpen(true);
  };

  /**
   * Cancel returns the call to RINGING. The patient who opened the reasons out
   * of curiosity must not lose the consultation for it, so this clears the draft
   * and restores the ring rather than declining.
   */
  const cancelReasons = () => {
    setReasonOpen(false);
    setOtherOpen(false);
    setNote('');
    // The reasons panel went away, so the focus inside it did too: hand it back
    // to the primary action rather than dropping the user on the page body.
    acceptRef.current?.focus();
    if (!ringBlocked) onEnableSound();
  };

  /** Leave the note field but stay in the reasons (back to the chips). */
  const backToChips = () => {
    setOtherOpen(false);
    setNote('');
  };

  const sendOther = () => onDecline('OTHER', note.trim() || null);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== 'Escape') return;
    if (otherOpen) {
      event.stopPropagation();
      backToChips();
      return;
    }
    if (reasonOpen) {
      event.stopPropagation();
      cancelReasons();
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={t('incomingCall.title')}
      data-testid="incoming-call"
      data-ringing={ringBlocked ? 'blocked' : 'true'}
      data-declining={reasonOpen ? 'true' : 'false'}
      onKeyDown={handleKeyDown}
    >
      <div className="w-full max-w-sm max-h-[90vh] overflow-y-auto bg-white rounded-2xl shadow-2xl p-5 animate-slide-up">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-primary-50 flex items-center justify-center flex-shrink-0">
            {call.fromRole === 'doctor'
              ? <Stethoscope size={22} className="text-primary-500" />
              : <User size={22} className="text-primary-500" />}
          </div>
          <div className="min-w-0">
            <div className="text-xs font-semibold uppercase tracking-wide text-primary-500">
              {t('incomingCall.title')}
            </div>
            <div className="text-base font-bold text-text-primary truncate">
              {t('incomingCall.calling', { name: caller })}
            </div>
            {roleLabelKey && (
              <div className="text-xs text-text-muted">{t(roleLabelKey)}</div>
            )}
          </div>
        </div>

        <div className="mt-3 text-sm text-text-secondary">
          {t('incomingCall.booking', { id: call.bookingId })}
        </div>

        {ringBlocked && (
          <div className="mt-3 flex items-start gap-2 rounded-xl bg-warning-50 p-3" role="alert">
            <AlertTriangle size={15} className="text-warning-600 flex-shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <p className="text-xs text-text-secondary">{t('incomingCall.soundBlocked')}</p>
              <button
                type="button"
                onClick={onEnableSound}
                className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-primary-600 hover:text-primary-700"
              >
                <Volume2 size={13} /> {t('incomingCall.enableSound')}
              </button>
            </div>
          </div>
        )}

        <div className="mt-4 flex items-center gap-2">
          <button
            ref={acceptRef}
            type="button"
            onClick={onAccept}
            className="btn btn-primary flex-1 flex items-center justify-center gap-2 min-h-[44px]"
            aria-label={t('incomingCall.accept')}
          >
            <Phone size={16} /> {t('incomingCall.accept')}
          </button>
          {/* ONE tap, no reason required — the certified decline behaviour. */}
          <button
            type="button"
            onClick={() => onDecline()}
            className="btn btn-ghost flex items-center justify-center gap-2 text-danger-600 min-h-[44px]"
            aria-label={t('incomingCall.decline')}
          >
            <PhoneOff size={16} /> {t('incomingCall.decline')}
          </button>
        </div>

        {/* ── Optional reasons: opt-in, one tap each, cancellable ─────────── */}
        {!reasonOpen ? (
          <button
            type="button"
            onClick={openReasons}
            aria-expanded={false}
            aria-controls="decline-reasons"
            className="mt-2 min-h-[40px] text-xs font-semibold text-text-secondary underline underline-offset-2 decoration-surface-300 hover:text-primary-600"
          >
            {t('callDecline.addReason')}
          </button>
        ) : (
          <div id="decline-reasons" className="mt-3 border-t border-surface-100 pt-3">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="text-xs font-semibold text-text-secondary" id="decline-reasons-label">
                  {t('callDecline.title')}
                </div>
                <div className="text-[11px] text-text-muted">{t('callDecline.subtitle', { name: caller })}</div>
              </div>
              {/* Cancel keeps the CALL: opening the reasons decides nothing. */}
              <button
                type="button"
                onClick={cancelReasons}
                aria-label={t('callDecline.cancel')}
                className="flex items-center gap-1 min-h-[40px] px-2 -mr-1 rounded text-xs font-semibold text-text-secondary hover:text-text-primary"
              >
                <X size={14} /> {t('callDecline.cancel')}
              </button>
            </div>

            <div
              ref={reasonsRef}
              className="mt-2 flex flex-wrap gap-1.5"
              role="group"
              aria-labelledby="decline-reasons-label"
            >
              {DECLINE_QUICK_REASON_KEYS.map(option => (
                <button
                  key={option.code}
                  type="button"
                  onClick={() => onDecline(option.code)}
                  className="min-h-[40px] px-3 rounded-full border border-surface-200 text-xs text-text-primary hover:bg-surface-50 transition-colors"
                >
                  {t(option.key)}
                </button>
              ))}
              <button
                type="button"
                onClick={() => { setOtherOpen(true); }}
                aria-pressed={otherOpen}
                className={`min-h-[40px] px-3 rounded-full border text-xs transition-colors ${
                  otherOpen
                    ? 'border-primary-400 bg-primary-50 text-primary-700 font-semibold'
                    : 'border-surface-200 text-text-primary hover:bg-surface-50'
                }`}
              >
                {t('callDecline.reason.other')}
              </button>
            </div>

            {otherOpen && (
              <div className="mt-3">
                <label className="label" htmlFor="decline-note">
                  {t('callDecline.noteLabel')}
                </label>
                <textarea
                  id="decline-note"
                  ref={noteRef}
                  value={note}
                  onChange={e => setNote(e.target.value.slice(0, DECLINE_NOTE_MAX_LENGTH))}
                  maxLength={DECLINE_NOTE_MAX_LENGTH}
                  rows={2}
                  className="input resize-none"
                  placeholder={t('callDecline.notePlaceholder')}
                  aria-describedby="decline-note-count"
                />
                <p id="decline-note-count" className="mt-1 text-[11px] text-text-muted">
                  {t('callDecline.noteCounter', { count: note.length, max: DECLINE_NOTE_MAX_LENGTH })}
                </p>
                <div className="mt-2 flex items-center gap-2">
                  <button
                    type="button"
                    onClick={sendOther}
                    className="btn btn-danger flex-1 min-h-[44px] flex items-center justify-center gap-2"
                  >
                    {t('callDecline.send')}
                  </button>
                  <button
                    type="button"
                    onClick={backToChips}
                    className="btn btn-ghost min-h-[44px] flex items-center justify-center gap-1.5"
                    aria-label={t('callDecline.back')}
                  >
                    <ArrowLeft size={16} /> {t('callDecline.back')}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default IncomingCallOverlay;
